import {
  Button,
  Descriptions,
  Empty,
  Flex,
  Tag,
  Timeline,
  Typography
} from 'antd'
import { ArrowRight } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { DateTimeCell, SoonTag } from 'src/components'
import { paths } from 'src/router'

import { CATEGORY_COLORS, EVENT_KIND_COLORS } from './constants'
import { useStyles } from './styles'
import type { GraphNode, LiveEvent } from './types'

const { Text } = Typography

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/

/** Deep-link target for a node, or null when no real page exists (mock domains). */
const routeForNode = (node: GraphNode, ws: string): string | null => {
  if (node.id === 'company') return paths.workspaces.details(ws)
  if (node.id === 'app-more') return paths.workspaces.apps.root(ws)
  if (node.id === 'user-more') return paths.workspaces.members(ws)
  if (node.id.startsWith('app-')) {
    return paths.workspaces.apps.details(ws, node.id.slice(4))
  }
  if (node.id.startsWith('user-')) {
    return paths.workspaces.membersPerson(ws, node.id.slice(5))
  }
  if (node.kind === 'agent') return paths.workspaces.agents(ws)

  return null
}

type Props = {
  node: GraphNode | null
  activity: LiveEvent[]
  neighbors: GraphNode[]
  workspaceIdParam: string
  onClose: () => void
  onSelectNode: (id: string) => void
}

export const NodeDetailPanel = ({
  node,
  activity,
  neighbors,
  workspaceIdParam,
  onClose,
  onSelectNode
}: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const route = node ? routeForNode(node, workspaceIdParam) : null

  useEffect(() => {
    if (!node) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [node, onClose])

  const lastActive = activity[0]?.ts

  if (!node) return null

  return (
    <aside className={styles.detail}>
      {node ? (
        <Flex vertical gap={16}>
          <Flex align="center" gap={9} className={styles.detailHead}>
            <span
              className={styles.detailDot}
              style={{ background: CATEGORY_COLORS[node.kind] }}
              aria-hidden
            />
            <Text
              className={styles.detailName}
              style={{ flex: 1 }}
              ellipsis={{ tooltip: node.label }}
            >
              {node.label}
            </Text>
            <button
              type="button"
              className={styles.detailClose}
              onClick={onClose}
              aria-label={t('workspaceOverview.companyGraph.close')}
            >
              ×
            </button>
          </Flex>

          <Flex align="center" gap={8} wrap="wrap" style={{ marginTop: -6 }}>
            <Tag style={{ fontSize: 10, marginInlineEnd: 0 }}>
              {t(`workspaceOverview.companyGraph.legend.${node.kind}`)}
            </Tag>
            {node.sample ? (
              <SoonTag>{t('workspaceOverview.companyGraph.sample')}</SoonTag>
            ) : null}
            <span className={styles.statusPill}>
              <span className={styles.statusDot} aria-hidden />
              {t('workspaceOverview.companyGraph.active')}
            </span>
          </Flex>

          {lastActive ? (
            <Text type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
              {t('workspaceOverview.companyGraph.lastActive')}:{' '}
              <DateTimeCell value={lastActive} variant="relative" />
            </Text>
          ) : null}

          {route ? (
            <Button
              type="primary"
              ghost
              size="small"
              block
              icon={<ArrowRight size={13} />}
              iconPlacement="end"
              onClick={() => navigate(route)}
            >
              {t('workspaceOverview.companyGraph.viewDetails')}
            </Button>
          ) : null}

          {node.facts.length > 0 ? (
            <div>
              <div className={styles.sectionLabel}>
                {t('workspaceOverview.companyGraph.details')}
              </div>
              <Descriptions
                column={1}
                size="small"
                items={node.facts.map((fact, i) => ({
                  key: `${fact.label}-${i}`,
                  label: fact.label,
                  children: ISO_DATE.test(fact.value) ? (
                    <DateTimeCell value={fact.value} variant="date" />
                  ) : (
                    fact.value
                  )
                }))}
              />
            </div>
          ) : null}

          <div>
            <div className={styles.sectionLabel}>
              {t('workspaceOverview.companyGraph.recentActivity')}
            </div>
            {activity.length > 0 ? (
              <Timeline
                items={activity.map((ev) => ({
                  key: ev.id,
                  color: EVENT_KIND_COLORS[ev.kind],
                  children: (
                    <Flex vertical gap={1}>
                      <Text style={{ fontSize: 12.5 }}>{ev.text}</Text>
                      <DateTimeCell
                        value={ev.ts}
                        variant="relative"
                        className={styles.timelineWhen}
                      />
                    </Flex>
                  )
                }))}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('workspaceOverview.companyGraph.noActivity')}
              />
            )}
          </div>

          {neighbors.length > 0 ? (
            <div>
              <div className={styles.sectionLabel}>
                {t('workspaceOverview.companyGraph.connections')}
              </div>
              <div>
                {neighbors.map((n) => (
                  <span
                    key={n.id}
                    className={styles.connChip}
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectNode(n.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') onSelectNode(n.id)
                    }}
                  >
                    <span
                      className={styles.connSwatch}
                      style={{ background: CATEGORY_COLORS[n.kind] }}
                      aria-hidden
                    />
                    {n.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </Flex>
      ) : null}
    </aside>
  )
}
